/**
 * V2 Asset Reference utilities
 *
 * Converts V1 AssetUrl (from StorageService/OnboardingMediaUploadService)
 * to V2 AssetReference format used by schema-models/v2.
 *
 * V1 AssetUrl: { thumb, small, medium, normal, origin }  — 5 resolution keys
 * V2 AssetReference: { original, small, name? }           — 2 keys
 *
 * Key rename: V1 "origin" → V2 "original"
 */

import type { AssetUrl } from '@modules/firebase-core/services/OnboardingMediaUploadService';

/** V2 image asset reference (original + small thumbnail) */
export interface V2AssetReference {
  original: string;
  /** Absent for URL-only references (user pasted a link — no resized variants). */
  small?: string;
  name?: string;
}

/**
 * Convert V1 AssetUrl (from upload service) to V2 AssetReference format.
 * Maps origin → original, preserves small.
 */
export function toV2AssetReference(assetUrl: Pick<AssetUrl, 'origin'> & Partial<AssetUrl>, name?: string): V2AssetReference {
  return {
    original: assetUrl.origin,
    small: assetUrl.small,
    ...(name ? { name } : {}),
  };
}

/**
 * Convert V1 AssetUrl to V2 video asset reference (original only, no thumbnail).
 */
export function toV2VideoAssetReference(assetUrl: AssetUrl): { original: string } {
  return {
    original: assetUrl.origin,
  };
}

/**
 * Read the primary URL from either V1 or V2 asset format.
 * Tries V2 "original" first, falls back to V1 "origin" / "normal" for legacy data.
 */
export function readAssetUrl(asset: Record<string, string> | string | null | undefined): string | null {
  if (typeof asset === 'string') return asset;
  if (!asset || typeof asset !== 'object') return null;
  return asset.original ?? asset.origin ?? asset.normal ?? null;
}
